/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs'
import difference from 'lodash/difference'
import uniqBy from 'lodash/uniqBy'

import { FORM_ORDER_OMIT_FIELDS } from 'constants/api'
import { formatDate, toDate } from 'constants/formats'
import { Diff, EventShirtDetailsProps } from 'constants/types'
import { Priority } from 'models/API'

import { generateAdminFees } from './PaymongoUtils'
import {
  DEFAULT_PARTICIPANT_COUNT,
  EVENT_SHIRT_DETAILS,
  SHIRT_KEY,
  TEAM_TYPES,
} from '../constants/models'

export const isEventSoldOut = (items) => {
  const totalMaxParticipants = items.reduce(
    (total, i) => total + (i.maxParticipants || 0),
    0,
  )
  const totalCount = items.reduce(
    (total, i) => total + (i.categoryCount?.['count'] || 0),
    0,
  )
  return totalMaxParticipants && totalCount >= totalMaxParticipants
}

interface FullShirtDetails {
  hasShirt2: boolean
  shirtDetails: EventShirtDetailsProps
}
export const hasShirtType = (
  fullShirtDetails: FullShirtDetails,
  type = SHIRT_KEY.SHIRT1,
) => {
  if (!fullShirtDetails) return false
  if (type === SHIRT_KEY.SHIRT1) {
    return 'TYPE_OPTIONS' in fullShirtDetails.shirtDetails.SHIRT1
  }

  if (fullShirtDetails.hasShirt2) {
    return 'TYPE_OPTIONS' in fullShirtDetails.shirtDetails.SHIRT2
  }

  return false
}

export const isCategorySoldOut = (count, maxParticipants) => {
  return maxParticipants && count >= maxParticipants
}

export const isCategoryRelay = (value) =>
  ([TEAM_TYPES.DUATHLON, TEAM_TYPES.TRIATHLON].includes(value.teamType) ||
    !!value.isTriathlon) &&
  !value.isIndividual

export const isCategoryMultisports = (value) =>
  !!value.isTriathlon || isCategoryRelay(value)

export const getBonusCategory = (cartItems, categories) => {
  let id = ''
  let count = 0

  const uniqueCatId = uniqBy(cartItems, 'categoryId')
  if (uniqueCatId.length === 1) {
    const currentCat = Object.values(categories).filter(
      (c) =>
        (c as unknown as any).id ===
        (uniqueCatId[0] as unknown as any).categoryId,
    )

    if (
      currentCat.length &&
      (currentCat[0] as unknown as any).bonusParticipants
    ) {
      id = (uniqueCatId[0] as unknown as any).categoryId
      count = (currentCat[0] as unknown as any).bonusParticipants
    }
  }

  return { id, count }
}

const getCartItemFees = (item, categories, paymentMode) => {
  // const fee =
  //   item.categoryId in categories ? categories[item.categoryId].fee : item.fee
  // const adminFee =
  //   item.categoryId in categories
  //     ? categories[item.categoryId].adminFees[paymentMode]
  //     : item.adminFee

  let fee = item.fee
  if (item.categoryId in categories) {
    fee =
      categories[item.categoryId].earlyBirdFee &&
      dayjs().isSameOrBefore(dayjs(categories[item.categoryId].earlyBirdUntil))
        ? categories[item.categoryId].earlyBirdFee
        : categories[item.categoryId].fee
  }

  const adminFee = generateAdminFees(fee)[paymentMode]
  return { fee, adminFee }
}

export const generateCartItems = (cartItems, categories, paymentMode) => {
  const newCartItems = []
  const { count: bonusCount } = getBonusCategory(cartItems, categories)

  const catMultisports =
    cartItems.filter((cartItem) => isCategoryMultisports(cartItem))?.[0] || []
  const isMultisports = Object.keys(catMultisports).length > 0

  // teamType categories
  const catTeam =
    cartItems.filter((cartItem) =>
      Object.keys(TEAM_TYPES).includes(cartItem.teamType),
    )?.[0] || []
  const isTeam = Object.keys(catTeam).length > 0

  let total = 0

  if (isMultisports) {
    const { fee, adminFee } = getCartItemFees(
      catMultisports,
      categories,
      paymentMode,
    )
    total = fee + adminFee
  } else if (isTeam) {
    const { fee, adminFee } = getCartItemFees(catTeam, categories, paymentMode)
    total = fee + adminFee
  }

  cartItems.forEach((item, index) => {
    let isBonus = false
    if (cartItems.length > DEFAULT_PARTICIPANT_COUNT && bonusCount > 0)
      isBonus = index + 1 > DEFAULT_PARTICIPANT_COUNT

    // const isBonus = cartItems.length - bonusCount - 1 < index
    const { fee, adminFee } = getCartItemFees(item, categories, paymentMode)
    newCartItems.push({
      ...item,
      fee,
      adminFee,
      isBonus,
    })

    if (!isMultisports && !isTeam && !isBonus)
      total += (fee + adminFee) * item.quantity
  })

  return { items: newCartItems, total, bonusCount }
}

const itemHasEarlyBird = (category, dt?) =>
  category.earlyBirdUntil &&
  dayjs(dt).isSameOrBefore(dayjs(category.earlyBirdUntil))

export const noCategoryPromotions = (category) =>
  !category.promotions?.items || category.promotions?.items.length === 0

const getCategoryPromotions = (category, dt?) => {
  if (noCategoryPromotions(category)) return null

  const currentPromo = category.promotions.items.find(
    (promo) =>
      dayjs(dt).isSameOrBefore(dayjs(promo.endDateTime)) &&
      dayjs(dt).isSameOrAfter(dayjs(promo.startDateTime)),
  )

  return currentPromo
}

export const getItemFee = (category) => {
  let fee = category.fee
  let adminFees = generateAdminFees(category.fee)
  if (itemHasEarlyBird(category)) {
    fee = category.earlyBirdFee
    if ('earlyBirdFee' in adminFees)
      adminFees = generateAdminFees(category.earlyBirdFee)
  }

  const currentPromo = getCategoryPromotions(category)
  if (currentPromo) {
    if (currentPromo.discountType === 'PERCENTAGE') {
      const discountedFee =
        category.fee - (category.fee * currentPromo.discount) / 100
      fee = discountedFee
      adminFees = generateAdminFees(discountedFee)
    } else if (currentPromo.discountType === 'FIXED') {
      const discountedFee = category.fee - currentPromo.discount
      fee = discountedFee
      adminFees = generateAdminFees(discountedFee)
    }
  }

  return { adminFees, fee }
}

export const categoryHasPromotions = (category, dt?) => {
  // TODO: eventually we will get rid of early bird and just use promotions, but for now we will keep it
  if (itemHasEarlyBird(category, dt)) return true

  return !!getCategoryPromotions(category, dt)
}

// For Download Participants
export const getParticipantPromotion = (category, dt?) => {
  if (itemHasEarlyBird(category, dt)) {
    return 'Early Bird'
  }

  const applicablePromo = getCategoryPromotions(category, dt)
  return applicablePromo ? applicablePromo.name : ''
}

export const getUserOnsiteEvents = (attributes) => {
  if (!attributes) return []
  if (!('custom:onsite_events' in attributes)) return []

  const events = attributes['custom:onsite_events'].split(',')
  return events
}

export const generateSelectOptions = (data: any[]) =>
  data
    .sort((a: Priority, b: Priority) => {
      if (!!a.priority && !b.priority) return -1
      if (!!b.priority && !a.priority) return 1
      return a.priority - b.priority
    })
    .map((event: Priority) => ({
      value: event.id,
      text: event.name,
    }))

export const CONVERT_SIZE = {
  _3XS: '3XS',
  _2XS: '2XS',
  XXXS: '3XS',
  XXS: '2XS',
  _2XL: '2XL',
  _3XL: '3XL',
  _4XL: '4XL',
  XXL: '2XL',
  XXXL: '3XL',
  XXXXL: '4XL',
}

const CONVERT_SIZE_API = {
  XXXS: '_3XS',
  XXS: '_2XS',
  XXL: '_2XL',
  XXXL: '_3XL',
  XXXXL: '_4XL',
}

export const getShirtSize = (size, toApi = false) => {
  if (toApi) return size in CONVERT_SIZE_API ? CONVERT_SIZE_API[size] : size
  return size in CONVERT_SIZE ? CONVERT_SIZE[size] : size
}

export const generateInitFormParticipant = (participant, event, category) => {
  const fullShirtDetails = {
    hasShirt2: !!category.hasShirt2,
    shirtDetails: event.shirtDetails ?? EVENT_SHIRT_DETAILS,
  }
  const tandemInfo = {
    isTandem: !!category.isTandem,
    tandemAgeRange: category.tandemAgeRange ?? '',
    eventEndDateTime: event.endDateTime,
  }

  return {
    // other fields
    email: participant.email,
    firstParticipantInfo: participant.firstParticipantInfo,
    mandatoryCheck: participant.mandatoryCheck,

    // for category form
    categoryId: participant.categoryId,

    // relayEvent: participant.relayEvent?.split(',') ?? [],
    relayEvent: Array.isArray(participant.relayEvent)
      ? participant.relayEvent
      : participant.relayEvent?.split(',') ?? [],
    teamType: category.teamType ?? '',
    isTriathlon: !!category.isTriathlon,
    isIndividual: !!category.isIndividual,

    shirtSize: getShirtSize(participant.shirtSize, true),
    ...(hasShirtType(fullShirtDetails) && {
      shirtType: participant.shirtType,
    }),

    hasShirt2: !!category?.hasShirt2,
    ...(category?.hasShirt2 && {
      shirtSize2: getShirtSize(participant.shirtSize2, true),
      ...(hasShirtType(fullShirtDetails, SHIRT_KEY.SHIRT2) && {
        shirtType2: participant.shirtType2,
      }),
    }),
    ...fullShirtDetails,

    hasJersey: !!category?.hasJersey,
    ...(!!category?.hasJersey && {
      jerseySize: getShirtSize(participant.jerseySize, true),
    }),

    teamName: participant.teamName ?? '',

    hasBibName: !!event?.hasBibName,
    ...(!!event?.hasBibName && {
      bibName: participant.bibName ?? '',
    }),

    subCategories: event.subCategories,
    ...(event.subCategories && {
      subCategory: participant.subCategory,
    }),

    // form participant form
    firstName: participant.firstName,
    lastName: participant.lastName,
    birthdate: toDate(participant.birthdate),
    nationality: participant.nationality ?? 'PH',
    gender: participant.gender,
    address: participant.address,
    phoneNumber: participant.phoneNumber,

    // for tandem form
    ...tandemInfo,
    ...(tandemInfo.isTandem && {
      tandemName: participant.tandemName,
      tandemBirthdate: participant.tandemBirthdate
        ? toDate(participant.tandemBirthdate)
        : null,
      tandemShirtSize: getShirtSize(participant.tandemShirtSize, true),
    }),

    // emergency contact form
    emergencyContactName: participant.emergencyContactName,
    emergencyContactPhone: participant.emergencyContactPhone,

    // ...participant,
    // // categoryId: selectedCategory.id,
    // teamName: participant.teamName ?? '',
    // bibName: participant.bibName ?? '',
    // subCategory: participant.subCategory ?? '',
    // jerseySize: participant.jerseySize ?? '',
    // shirtType: participant.shirtType ?? '',
    // shirtType2: participant.shirtType2 ?? '',
    // shirtSize2: participant.shirtSize2 ?? '',
    // relayEvent: participant.relayEvent?.split(',') ?? [],
    // birthdate: toDate(participant.birthdate),

    // ...tandemInfo,
    // tandemName: participant.tandemName ?? '',
    // tandemBirthdate: participant.tandemBirthdate
    //   ? toDate(participant.tandemBirthdate)
    //   : '',
    // tandemShirtSize: participant.tandemShirtSize ?? '',

    // isTriathlon: !!selectedCategory.isTriathlon,
    // isIndividual: !!selectedCategory.isIndividual,
    // isOnsiteRegistration: true,
    // firstParticipants: event.firstParticipants
    //   ? {
    //       ...(event.firstParticipants &&
    //         JSON.parse(JSON.parse(event.firstParticipants as string))),
    //       TOTAL: getTotalParticipants,
    //     }
    //   : null,

    // subCategories: event.subCategories || null,
    // teamType: selectedCategory.teamType ?? '',
    // hasJersey: !!selectedCategory.hasJersey,
    // hasBibName: !!event.hasBibName,

    // ...fullShirtDetails,
  }
}

const PARTICIPANT_FIELDS = [
  'bibName',
  'bibNumber',
  'firstName',
  'lastName',
  'emergencyContactName',
  'emergencyContactPhone',
  'teamName',
  'categoryId',
  'subCategory',
  'shirtSize',
  'shirtType',
  'shirtSize2',
  'shirtType2',
  'jerseySize',
  'birthdate',
  'gender',
  'nationality',
  'email',
  'phoneNumber',
  'address',
  'tandemName',
  'tandemBirthdate',
  'tandemShirtSize',
  'additionalId',
  'firstParticipantInfo',
  'relayEvent',
  'mandatoryCheck',
]

const DATE_FORMAT = 'YYYY-MM-DD'
const getDate = (value) => {
  if (!value) return null

  return typeof value === 'string' ? value : formatDate(value, DATE_FORMAT)
}

export const getParticipantDataDiff = (objA, objB) => {
  const diff: Diff = { oldData: {}, newData: {} }

  const OMIT_FIELDS = difference(FORM_ORDER_OMIT_FIELDS, ['jerseySize'])
  const filterFields = (key: string) =>
    PARTICIPANT_FIELDS.includes(key) && !OMIT_FIELDS.includes(key)

  const doBirthdateCheck = (key: string) => {
    const dateA = getDate(objA[key])
    const dateB = getDate(objB[key])

    if (!Object.prototype.hasOwnProperty.call(objB, key) || dateA !== dateB) {
      diff.oldData[key] = dateA
      diff.newData[key] = dateB
    }
  }

  // Find keys in A that are different in B or missing in B
  Object.keys(objA)
    .filter(filterFields)
    .forEach((key) => {
      if (['birthdate', 'tandemBirthdate'].includes(key)) {
        doBirthdateCheck(key)
      } else if (['relayEvent'].includes(key)) {
        if (
          !Object.prototype.hasOwnProperty.call(objB, key) ||
          (objA[key].join(',') ?? '') !== objB[key].join(',')
        ) {
          diff.oldData[key] = objA[key].join(',') ?? ''
          diff.newData[key] =
            objB[key].join(',').length > 0 ? objB[key].join(',') : null
        }
      } else {
        if (
          !Object.prototype.hasOwnProperty.call(objB, key) ||
          (objA[key] ?? '') !== (objB[key] ?? '')
        ) {
          diff.oldData[key] = objA[key]
          diff.newData[key] = objB[key]
        }
      }
    })

  // Find keys in B that are missing in A (the 'd: 5' case)
  Object.keys(objB)
    .filter(filterFields)
    .forEach((key) => {
      if (['birthdate', 'tandemBirthdate'].includes(key)) {
        doBirthdateCheck(key)
      } else if (!Object.prototype.hasOwnProperty.call(objA, key)) {
        diff.oldData[key] = objA[key]
        diff.newData[key] = objB[key]
      }
    })

  return diff
}
